import type { SelectionnerOngletsIn, SelectionnerOngletsOut } from '../ajouter/CreationDemandeConfiguration.types.ts'

import {
  type DemandeCloneDTO,
  type DemandeCreateDTO,
  type HabilitationDispositifDTO,
  type HabilitationsDTO,
  ONGLET_EMPLOYEUR,
} from '@/features/api/extranet/dto/Demande/shared'
import type { IdccDTO } from '@/features/api/extranet/dto/Lov'
import type { RattachementDTO } from '@/features/api/extranet/dto/Profil'
import type { EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'
import type {
  MachineEtatOptions,
  Runnable,
  Transition,
  TransitionOut,
  Transitions,
} from '@/features/domaine/machine-etats'
import { type NiveauPermission } from '@/features/domaine/types/opco'
import { useMachineEtatDisplay } from '@/features/interface/machine-etats/useMachineEtat.ts'
import allServices from '@/features/services/demande/DemandeAllService.ts'
import { loadRattachements } from '@/features/services/profil/RattachementUtilisateurServices.ts'

import { idccBudgetBloque } from '@/features/domaine/regles-akto/shared'
import { SousDispositif } from '@/features/domaine/types/opco/Demande/OUTILFPE/SousDispositif.ts'
import ChoixEtablissementModeAdherent from '@/features/interface/demande/ajouter/cartes/ChoixEtablissementModeAdherent.vue'
import ChoixIdcc from '@/features/interface/demande/ajouter/cartes/ChoixIdcc.vue'
import ChoixOnglets from '@/features/interface/demande/ajouter/cartes/ChoixOnglets.vue'
import ChoixRattachement from '@/features/interface/demande/ajouter/cartes/ChoixRattachement.vue'
import ChoixSousDispositif from '@/features/interface/demande/ajouter/cartes/ChoixSousDispositif.vue'
import { fabriqueRunCarte } from '@/features/interface/machine-etats/tools.ts'
import InformationEtablissementInactif from '../ajouter/cartes/InformationEtablissementInactif.vue'
import InformationIdccBudget from '../ajouter/cartes/InformationIdccBudget.vue'

const ETAT_RATTACHEMENT_CHARGÉS = 'ETAT_RATTACHEMENT_CHARGÉS' as const
const transitionChargerRattachements: Runnable<void, RattachementDTO[]> = {
  run: async () => {
    const rattachements = (await loadRattachements(true))
      ?.filter((rattachement) =>
        rattachement.publics.includes('Interimaire'),
      )
      ?.filter((rattachement) =>
        rattachement.permissions.some((permission) => permission.type === 'FPE' && permission.niveau === 'Ecriture'),
      )
    return {
      out: rattachements ?? [],
      type: 'automatique',
    }
  },
}

const ETAT_RATTACHEMENT_SELECTIONNÉ = 'ETAT_RATTACHEMENT_SELECTIONNÉ' as const
const transitionSelectionnerRattachements: Runnable<RattachementDTO[], RattachementDTO> = {
  run: async (options) => {
    if (options.modelIn.length === 1) {
      return {
        out: options.modelIn[0]!,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixRattachement, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<RattachementDTO>
  },
}

const ETAT_ETABLISSEMENT_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_SELECTIONNÉ' as const
const transitionSelectionnerEtablissement: Runnable<RattachementDTO, EtablissementAdherentDTO> = {
  run: async (options) => {
    // console.log(options.modelIn.typeEntiteJuridique)
    if (options.modelIn.typeEntiteJuridique === 'Etablissement') {
      const etablissement = (
        await allServices.searchEtablissementAdherent(options.modelIn.identifiantEntiteJuridique)!
      ).at(0)!
      return {
        out: etablissement,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixEtablissementModeAdherent, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<EtablissementAdherentDTO>
  },
}

const ETAT_IDCC_SELECTIONNÉ = 'ETAT_IDCC_SELECTIONNÉ' as const
const transitionSelectionnerIdcc: Runnable<EtablissementAdherentDTO, IdccDTO> = {
  run: async (options) => {
    if (options.modelIn.idcc) {
      return {
        out: options.modelIn.idcc,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixIdcc, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    } as TransitionOut<IdccDTO>
  },
}

const ETAT_HABILITATIONS_CHARGÉES = 'ETAT_HABILITATIONS_CHARGÉES' as const
export type TransitionChargementHabilitationsIn = { siret: string; idcc: string }
const transitionChargementHabilitations: Runnable<TransitionChargementHabilitationsIn, HabilitationsDTO> = {
  run: async (options) => {
    const habilitations =
      await allServices.getHabilitations('SpecifiqueTT', options.modelIn.siret, options.modelIn.idcc)
    return {
      out: habilitations,
      type: 'automatique',
    }
  },
}

const ETAT_SOUS_DISPOSITIF_SELECTIONNÉ = 'ETAT_SOUS_DISPOSITIF_SELECTIONNÉ' as const
const transitionSelectionnerSousDispositif: Runnable<HabilitationDispositifDTO, string | null> = {
  run: async (options) => {
    // console.log(options.modelIn.sousDispositifs)
    if (!options.modelIn.sousDispositifs?.length) {
      return {
        out: null,
        type: 'automatique',
      }
    } else if (options.modelIn.sousDispositifs.length === 1) {
      return {
        out: options.modelIn.sousDispositifs[0]!,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: ChoixSousDispositif, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ = 'ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ' as const
const transitionEtablissementInactif: Runnable<EtablissementAdherentDTO, EtablissementAdherentDTO> = {
  run: async (options) => {
    if (options.modelIn.etatAdministratif === 'A') {
      return {
        out: options.modelIn,
        type: 'automatique',
      }
    }

    const display = useMachineEtatDisplay()
    const result = await display.runComponent({ component: InformationEtablissementInactif, ...options })

    return {
      out: result.value,
      transitionState: result.transitionState,
      type: 'interactif',
    }
  },
}

const ETAT_IDCC_BUDGET_SELECTIONNÉ = 'ETAT_IDCC_BUDGET_SELECTIONNÉ' as const
const transitionSelectionnerIdccBudget: Runnable<void, void> = {
  run: fabriqueRunCarte(InformationIdccBudget),
}

/* Spécifiques clonnage */
const ETAT_HABILITATIONS_SOURCE_CHARGÉES = 'ETAT_HABILITATIONS_SOURCE_CHARGÉES' as const

const ETAT_ONGLETS_SELECTIONNÉS = 'ETAT_ONGLETS_SELECTIONNÉS' as const
const transitionSelectionnerOnglets: Runnable<SelectionnerOngletsIn, SelectionnerOngletsOut> = {
  run: fabriqueRunCarte(ChoixOnglets),
}

const transitionChargementEtablissementOnglet: Runnable<string, EtablissementAdherentDTO> = {
  run: async (options) => {
    const etablissement = (await allServices.searchEtablissementAdherent(options.modelIn)!).at(0)!
    return {
      out: etablissement,
      type: 'automatique',
    }
  },
}

/* Transitions finales, communes à Creation et Clonnage */
const transitionsFinales: Transitions<void | CloneDemandeLaunch> = [
  {
    etatDepart: ETAT_HABILITATIONS_CHARGÉES,
    runnable: transitionSelectionnerSousDispositif,
    etatArrive: ETAT_SOUS_DISPOSITIF_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      const habilitations = etats[ETAT_HABILITATIONS_CHARGÉES] as HabilitationsDTO
      const dispositif =
        habilitations.dispositifs.find(item => item.code === 'FPE')!
      dispositif.sousDispositifs = dispositif.sousDispositifs.filter((sousDispositif) =>
        SousDispositif.includes(sousDispositif as SousDispositif),
      )

      // Exclue les sous-dispositifs non-autorisés
      const sousDispositifsExclues = `${import.meta.env.VITE_SOUS_DISPOSITIFS_EXCLUS}`
        .split(',')
        .map((s) => s.trim())
        .filter((s) => s)

      if (sousDispositifsExclues.length > 0) {
        dispositif.sousDispositifs = dispositif.sousDispositifs.filter(
          (sousDispositif) => !sousDispositifsExclues.includes(sousDispositif),
        )
      }

      dispositif.typeDemande = 'SpecifiqueTT'
      return dispositif
    },
  },
  {
    etatDepart: [ETAT_SOUS_DISPOSITIF_SELECTIONNÉ],
    runnable: transitionSelectionnerIdccBudget,
    etatArrive: ETAT_IDCC_BUDGET_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO
    },
    if: ({ etats }) =>
      etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] === 'PDC' &&
      idccBudgetBloque.includes(etats[ETAT_IDCC_SELECTIONNÉ]!.code),
  },
]

const transitionsTTCreation: Transitions<void> = [
  /* Pour l'adhérent le process démarre ici */
  {
    etatDepart: null,
    runnable: transitionChargerRattachements,
    etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
  },
  {
    etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
    runnable: transitionSelectionnerRattachements,
    etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_RATTACHEMENT_CHARGÉS]
    },
  },
  {
    etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerEtablissement,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_RATTACHEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionEtablissementInactif,
    etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    runnable: transitionSelectionnerIdcc,
    etatArrive: ETAT_IDCC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_CHARGÉES,
    queryIn: ({ etats }) => {
      return {
        siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur,
        idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      }
    },
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO>,
  ...transitionsFinales,
] as const

export const CreationDemandeTTConfigurations: MachineEtatOptions<void, DemandeCreateDTO> = {
  transitions: transitionsTTCreation,
  resultCallback: ({ etats }) => {
    return {
      typeDemande: 'SpecifiqueTT',
      dispositif: 'FPE',
      sousDispositif: etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] as string,
      siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur!,
      idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      typePublic: 'Interimaire',
      consentementTraitementDonnees: true,
    } as DemandeCreateDTO
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}

export type CloneDemandeLaunch = {
  idDemande: number
  siret: string
  idcc: string
  permissions: NiveauPermission
}
const transitionsTTClone: Transitions<CloneDemandeLaunch> = [
  /* Lancement process adhérent */
  {
    etatDepart: null,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_SOURCE_CHARGÉES,
    queryIn: ({ launchParams }) => {
      return {
        siret: launchParams!.siret,
        idcc: launchParams!.idcc,
      }
    },
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO, CloneDemandeLaunch>,
  /* Lancement process prestataire */
  {
    etatDepart: [ETAT_HABILITATIONS_SOURCE_CHARGÉES],
    runnable: transitionSelectionnerOnglets,
    etatArrive: ETAT_ONGLETS_SELECTIONNÉS,
    queryIn: ({ launchParams, contextCourant, etats }) => {
      return {
        typeDemande: 'SpecifiqueTT',
        context: contextCourant,
        dispositif: 'FPE',
        habilitations: etats[ETAT_HABILITATIONS_SOURCE_CHARGÉES],
        permissions: launchParams?.permissions,
      }
    },
  } as Transition<SelectionnerOngletsIn, SelectionnerOngletsOut, CloneDemandeLaunch>,
  /* Selection etablissement mode Adhérent */
  {
    etatDepart: ETAT_ONGLETS_SELECTIONNÉS,
    runnable: transitionChargerRattachements,
    etatArrive: ETAT_RATTACHEMENT_CHARGÉS,
    if: ({ contextCourant, etats }) => {
      if (contextCourant === TypesContextesAutorisesEnum.Prestataire) return false
      return !(etats[ETAT_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR)
    },
  } as Transition<void, RattachementDTO[], CloneDemandeLaunch>,
  {
    etatDepart: ETAT_RATTACHEMENT_CHARGÉS,
    runnable: transitionSelectionnerRattachements,
    etatArrive: ETAT_RATTACHEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      const rattachements: RattachementDTO[] = etats[ETAT_RATTACHEMENT_CHARGÉS]
      return rattachements.filter((item) =>
        item.permissions.some(
          (permission) => permission.type === 'FPE' && permission.niveau === 'Ecriture',
        ),
      )
    },
  } as Transition<RattachementDTO[], RattachementDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_RATTACHEMENT_SELECTIONNÉ,
    runnable: transitionSelectionnerEtablissement,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ etats }) => etats[ETAT_RATTACHEMENT_SELECTIONNÉ],
  } as Transition<RattachementDTO, EtablissementAdherentDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_ONGLETS_SELECTIONNÉS,
    runnable: transitionChargementEtablissementOnglet,
    etatArrive: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    queryIn: ({ launchParams }) => launchParams?.siret,
    if: ({ etats }) => {
      return (etats[ETAT_ONGLETS_SELECTIONNÉS] as string[]).includes(ONGLET_EMPLOYEUR)
    },
  } as Transition<string, EtablissementAdherentDTO, CloneDemandeLaunch>,
  {
    etatDepart: ETAT_ETABLISSEMENT_SELECTIONNÉ,
    runnable: transitionEtablissementInactif,
    etatArrive: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_ETABLISSEMENT_INACTIF_SELECTIONNÉ,
    runnable: transitionSelectionnerIdcc,
    etatArrive: ETAT_IDCC_SELECTIONNÉ,
    queryIn: ({ etats }) => {
      return etats[ETAT_ETABLISSEMENT_SELECTIONNÉ]
    },
  },
  {
    etatDepart: ETAT_IDCC_SELECTIONNÉ,
    runnable: transitionChargementHabilitations,
    etatArrive: ETAT_HABILITATIONS_CHARGÉES,
    queryIn: ({ etats }) => {
      return {
        siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur,
        idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      }
    },
  } as Transition<TransitionChargementHabilitationsIn, HabilitationsDTO>,
  ...transitionsFinales,
]

export const CloneDemandeTTConfiguration: MachineEtatOptions<CloneDemandeLaunch, DemandeCloneDTO> = {
  transitions: transitionsTTClone,
  resultCallback: ({ etats, launchParams }) => {
    return {
      typeDemande: 'SpecifiqueTT',
      id: launchParams?.idDemande,
      dispositif: 'FPE',
      sousDispositif: (etats[ETAT_SOUS_DISPOSITIF_SELECTIONNÉ] as string | null) ?? undefined,
      siret: (etats[ETAT_ETABLISSEMENT_SELECTIONNÉ] as EtablissementAdherentDTO).siret.valeur!,
      idcc: (etats[ETAT_IDCC_SELECTIONNÉ] as IdccDTO).code,
      typePublic: 'Interimaire',
      consentementTraitementDonnees: true,
      onglets: etats[ETAT_ONGLETS_SELECTIONNÉS],
    } as DemandeCloneDTO
  },
  disposeCallback: () => useMachineEtatDisplay().hide(),
}
